/**
 * FloorPlanDesignSection — native lift of the legacy `FloorPlanDesign`
 * (color/style/orientation, property-type radios, fixtures/furniture/gardens).
 * All data wiring is rewritten onto the ops-hook `patch`; no constate.
 *
 * ⭐ The property-type picker lives here (legacy parity) — a real completeness gate
 * (`selectedPropertyType`).
 */

import React, { type ReactNode, useMemo } from 'react'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { CORAL_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { ColorThemePreview } from 'components/common/ColorThemePreview'
import { MUICheckCard } from 'components/common/MUICards'
import { MUIInputField } from 'components/common/MUIInputField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import { SystemMessage } from 'components/common/SystemMessage'
import type { ToggleFilterValue } from 'components/common/ToggleFilter'
import { PropertyType } from 'models/property'

import { FLOOR_PLAN_LINE_SETTINGS_KEY, type FloorPlanConfigDraftDTO } from '../floorPlanConfig.dto'
import { FloorPlanColors, type FloorPlanProductSettings, FloorPlanRenderType, Orientations, Styles } from 'constants/floorPlan'
import { FLOOR_PLAN_PRODUCT_COMMENT_MAX_LENGTH } from 'constants/purchaseFlow'
import { SelectableImageBox } from 'components/common/FloorPlanControls'
import { stylesMap } from 'constants/floorPlan'

const colorIcons: Record<FloorPlanColors, ReactNode> = {
  NEUTRAL: <ColorThemePreview mainColor="#9D9FA1" accentColor="#EFF0F0" />,
  BLACK: <ColorThemePreview mainColor="#252A2E" accentColor="white" />,
  BLUE: <ColorThemePreview mainColor="#274C78" accentColor="#E7ECEF" />,
  GREEN: <ColorThemePreview mainColor="#4D5D53" accentColor="#F4F4F4" />,
}

interface Props {
  value: FloorPlanConfigDraftDTO
  patch: (partial: Partial<FloorPlanConfigDraftDTO>) => void
  isEditing: boolean
  /** Reports a colour pick to analytics. Only the 2D colour row is reported,
   *  matching legacy — the 3D style row was never a `floor_plan_color_selected`. */
  onColorSelected?: (color: string) => void
}

/**
 * @component FloorPlanDesignSection
 * Design options (color/style/orientation, property type, fixtures/furniture/gardens).
 */
export const FloorPlanDesignSection: React.FC<Props> = ({ value, patch, isEditing, onColorSelected }) => {
  // A v2 line is one product, so its editing settings are the single entry of the legacy per-product map.
  const scope = Object.values(value.floorPlanProductConfig ?? {})[0] as FloorPlanProductSettings | undefined
  const patchScope = (partial: Partial<FloorPlanProductSettings>) => patch({
    floorPlanProductConfig: {
      [FLOOR_PLAN_LINE_SETTINGS_KEY]: { drawOnlySpecificUnit: undefined, drawOnlySpecificUnitComment: '', ...scope, ...partial },
    },
  })
  const { t } = useTranslation(['purchase_flow', 'purchase_flow_revamp'])

  const is2D = value.currentFloorPlanRenderType === FloorPlanRenderType.RENDER_2D
  const is3D = value.currentFloorPlanRenderType === FloorPlanRenderType.RENDER_3D

  const formatOrientationsValues: ToggleFilterValue<Orientations>[] = useMemo(
    () =>
      [Orientations.ANGLE_VIEW, Orientations.BIRD_VIEW].map((format) => ({
        value: format,
        displayName: t(`floor_plan_step.design.orientations.${format.toLowerCase()}`),
      })),
    [t]
  )

  return (
    <SectionedBorderBox title={t('floor_plan_step.design.title')}>
      <Box display="flex" flexDirection="column" gap={1}>

        {is2D && <>

          <Typography variant="text-sm" fontWeight={500} color={GRAY_700}>
            {t('floor_plan_step.design.color')}
          </Typography>

          <Stack display="flex" flexDirection="row" flexWrap="wrap" gap={1}>
            {Object.keys(FloorPlanColors).map((colorKey) =>
              <SelectableImageBox
                key={colorKey}
                width={12.5}
                height={8}
                isSelected={value.style === colorKey}
                onSelect={() => {
                  const color = FloorPlanColors[colorKey as keyof typeof FloorPlanColors]
                  patch({ style: color })
                  onColorSelected?.(color)
                }}
                image={colorIcons[colorKey as keyof typeof FloorPlanColors]}
                text={t(`floor_plan_step.design.colors.${colorKey}`)}
              />
            )}
          </Stack>

        </>}

        {is3D && <>

          <Typography variant="text-sm" fontWeight={500} color={GRAY_700}>
            {t('floor_plan_step.design.style')}
          </Typography>

          <Grid container spacing={1}>
            {Object.keys(Styles).map((styleKey) => (
              <Grid item key={styleKey}>
                <SelectableImageBox
                  width={15.4}
                  height={14.8}
                  isSelected={value.style === styleKey}
                  onSelect={() => patch({ style: Styles[styleKey as keyof typeof Styles] })}
                  image={stylesMap[styleKey as keyof typeof Styles]}
                  text={t(`floor_plan_step.design.styles.${styleKey}`)}
                />
              </Grid>
            ))}
          </Grid>

          <Typography variant="text-sm" fontWeight={500} color={GRAY_700}>
            {t('floor_plan_step.design.orientation')}
          </Typography>

          <SelectableButtonsGrid
            activeButtonClickable
            values={formatOrientationsValues}
            selectedValue={value.orientation ?? Orientations.BIRD_VIEW}
            onSelect={(format) => patch({ orientation: format })}
            onUnselect={(format) => patch({ orientation: format })}
          />

        </>}

        {/* Property type (Commercial / Residential) — a real completeness gate */}
        <Typography variant="text-sm" fontWeight={500} color={GRAY_700}>
          {t('floor_plan_step.design.property_type')}
        </Typography>

        <Box display="flex" flexDirection="column" gap={1} role="radiogroup" aria-label={t('floor_plan_step.design.property_type')}>
          <MUICheckCard
            isRadio
            checked={value.selectedPropertyType === PropertyType.COMMERCIAL}
            onCheck={() => patch({ selectedPropertyType: PropertyType.COMMERCIAL })}
          >
            <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
              {t('floor_plan_step.design.commercial')}
            </Typography>
          </MUICheckCard>

          <MUICheckCard
            isRadio
            checked={value.selectedPropertyType === PropertyType.RESIDENTIAL}
            onCheck={() => patch({ selectedPropertyType: PropertyType.RESIDENTIAL })}
          >
            <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
              {t('floor_plan_step.design.residential')}
            </Typography>
          </MUICheckCard>
        </Box>

        {!value.selectedPropertyType &&
          <Typography variant="text-xs" fontWeight={500} color={CORAL_600}>
            {t('floor_plan_step.design.property_type_error')}
          </Typography>
        }

        {/* What to draw from the client's plan: the whole of it, or one unit they name (legacy editing settings) */}
        {isEditing && <>

          <Typography variant="text-sm" fontWeight={500} color={GRAY_700}>
            {t('floor_plan_step.product_config.specificity_label')}
          </Typography>

          <Box display="flex" flexDirection="column" gap={1} role="radiogroup" aria-label={t('floor_plan_step.product_config.specificity_label')}>
            {[false, true].map((specificUnit) => (
              <MUICheckCard
                key={String(specificUnit)}
                isRadio
                checked={scope?.drawOnlySpecificUnit === specificUnit}
                onCheck={() => patchScope(
                  // The comment means something different for each option, so it starts empty after a switch
                  scope?.drawOnlySpecificUnit === specificUnit
                    ? { drawOnlySpecificUnit: specificUnit }
                    : { drawOnlySpecificUnit: specificUnit, drawOnlySpecificUnitComment: '' }
                )}
              >
                <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
                  {t(specificUnit ? 'floor_plan_step.product_config.specificity_specific_unit' : 'floor_plan_step.product_config.specificity_entire_plan')}
                </Typography>
              </MUICheckCard>
            ))}
          </Box>

          {scope?.drawOnlySpecificUnit !== undefined &&
            <MUIInputField
              isMultiline
              required={scope.drawOnlySpecificUnit}
              maxLength={FLOOR_PLAN_PRODUCT_COMMENT_MAX_LENGTH}
              label={t(scope.drawOnlySpecificUnit
                ? 'floor_plan_step.product_config.specificity_unit_comment_label'
                : 'floor_plan_step.product_config.specificity_entire_plan_comment_label')}
              value={scope.drawOnlySpecificUnitComment ?? ''}
              onChange={(e) => patchScope({ drawOnlySpecificUnitComment: e.target.value })}
            />
          }

        </>}

        {/* Elements (fixtures & furniture) */}
        <Typography variant="text-sm" fontWeight={500} color={GRAY_700}>
          {t('floor_plan_step.design.elements')}
        </Typography>

        <MUICheckCard
          checked={value.fixtures}
          onCheck={() => patch({ fixtures: !value.fixtures })}
        >
          <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.design.add_fixtures')}
          </Typography>
        </MUICheckCard>

        <MUICheckCard
          checked={value.furniture}
          onCheck={() => patch({ furniture: !value.furniture })}
        >
          <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
            {t('purchase_flow_revamp:floor_plan_config.add_furniture')}
          </Typography>
        </MUICheckCard>

        {is2D &&
          <MUICheckCard
            checked={!!value.gardensAndRoofSlopes}
            onCheck={() => patch({ gardensAndRoofSlopes: !value.gardensAndRoofSlopes })}
            expandableContent={isEditing
              ? <SystemMessage variant="system" message={t('floor_plan_step.design.add_gardens_and_roof_slopes_info')} />
              : undefined
            }
          >
            <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
              {t('floor_plan_step.design.add_gardens_and_roof_slopes')}
            </Typography>
          </MUICheckCard>
        }

        {is3D &&
          <SystemMessage message={t('floor_plan_step.design.disclaimer_furniture')} />
        }

      </Box>
    </SectionedBorderBox>
  )
}
